/**
 * Which of the two deployables owns a path.
 *
 * `naumu.ai` is ONE origin served by TWO containers: `apps/marketing` and
 * `apps/web`, split by path in Traefik (the path table in
 * `.design/INFRA_SPRINT_2026-08.md` is the contract; at the rewrite cutover the
 * same table becomes Cloudflare Origin Rules). This module is that table in
 * code, so both apps agree on where a link goes.
 *
 * Why it matters for links: a `next/link` to a path the CURRENT app does not
 * route is not a soft navigation - the App Router has no entry for it. Today
 * such a link still lands, but only because the two apps get different random
 * build IDs and Next falls back to a document navigation on mismatch. That is
 * an accident of the build, not a guarantee, and on the way it wastes an RSC
 * request and (with prefetch on) a prefetch against the OTHER container. Each
 * app's link bridge uses this predicate to render a plain `<a>` for foreign
 * paths instead.
 *
 * NEVER pin `generateBuildId` / `NEXT_DEPLOYMENT_ID` to a shared value across
 * the two apps - that would remove the accidental fallback and turn every
 * unconverted cross-app link into a client-side 404.
 */

/**
 * Path prefixes owned by `apps/marketing`. Order does not matter; matching is
 * "exact, or followed by `/`", so `/product` matches `/product` and
 * `/product/memory` but never `/products`. The exact root `/` is marketing's
 * too, but it is handled separately (a prefix `/` would swallow everything).
 *
 * This list is ONE HALF of a contract. The other half is the Traefik path
 * table: `docker/traefik/dynamic/naumu-dev.yml` locally, and the Dokploy domain
 * records on `marketing-*` plus the hand-added root router in prod
 * (`.claude/rules/deploy-topology.md`). Change one, change all of them;
 * `packages/platform-client/src/traefik-dev-routes.test.ts` guards the local
 * half.
 */
export const MARKETING_PATH_PREFIXES = [
	'/docs',
	'/blog',
	'/about',
	'/pricing',
	'/product',
	'/compare',
	'/use-cases',
	'/security',
	'/services',
	'/partners',
	'/legal',
	// Personal referral links (`/r/<code>`): validated and bounced to `/` by
	// the marketing app, which records the code on the touch record first.
	'/r',
	'/sitemap.xml',
	'/robots.txt',
	'/llms.txt',
	'/llms-full.txt',
	// Marketing's own asset namespaces: build output under the `/mkt`
	// assetPrefix, its `public/marketing/**` tree, and the brand logos.
	'/mkt',
	'/marketing',
	'/brand',
	// The self-hosted Figtree woff2 faces that `@naumu/ui`'s stylesheet requests
	// at an absolute `/fonts/...` URL. Both apps ship a copy in their own
	// `public/fonts` so each stays runnable alone; on the shared origin
	// marketing serves them (the files are byte-identical and content-immutable).
	'/fonts',
] as const;

/** Strips query and hash, and normalises a trailing slash off a non-root path. */
const normalise = (href: string): string => {
	const path = href.split('?')[0].split('#')[0];
	if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
	return path;
};

/**
 * True when `href` is served by `apps/marketing`.
 *
 * Only same-origin, absolute-from-root paths are classified. Anything else -
 * an external URL, a protocol-relative href, a `mailto:`/`tel:`, a relative
 * path, an empty string - returns false, which callers read as "not marketing"
 * and handle with their normal link logic.
 */
export const isMarketingPath = (href: string): boolean => {
	if (!href.startsWith('/') || href.startsWith('//')) return false;
	const path = normalise(href);
	if (path === '/' || path === '') return true;
	return MARKETING_PATH_PREFIXES.some(
		(prefix) => path === prefix || path.startsWith(`${prefix}/`),
	);
};

/**
 * True when `href` is served by `apps/web` (the platform container).
 *
 * The platform is the catch-all in Traefik, so this is the complement of
 * {@link isMarketingPath} over same-origin paths. Non-paths return false from
 * BOTH predicates - neither app claims them.
 */
export const isPlatformPath = (href: string): boolean => {
	if (!href.startsWith('/') || href.startsWith('//')) return false;
	return !isMarketingPath(href);
};

/** Trailing slash off, so `${origin}${href}` never doubles the separator. */
const trimTrailingSlash = (origin: string): string =>
	origin.length > 1 && origin.endsWith('/') ? origin.slice(0, -1) : origin;

/**
 * Origin of the OTHER app, or `null` when there isn't one (= same origin).
 *
 * There are NO dev fallbacks: local development runs both apps behind one
 * Traefik origin (`http://localhost:3000`, see `docker-compose.yml`), so a
 * cross-app href is a relative path there exactly as it is in prod. Setting one
 * of these vars is the escape hatch for running a single app alone or pointing
 * it at a remote origin.
 *
 * Both env names are read as literal `process.env.NEXT_PUBLIC_*` member
 * expressions on purpose: that is the only form Next's DefinePlugin inlines
 * into a client bundle. Never reach for them through a dynamic key.
 */
const foreignOrigin = (from: 'marketing' | 'platform'): string | null => {
	const configured =
		from === 'marketing'
			? process.env.NEXT_PUBLIC_PLATFORM_URL
			: process.env.NEXT_PUBLIC_MARKETING_URL;
	return configured ? trimTrailingSlash(configured) : null;
};

/**
 * The ONE seam for a cross-app href. Every link, `window.location.*` and
 * router navigation that can leave the current app must go through it.
 *
 * `from` is the app doing the navigating, NOT the app being navigated to.
 *
 *   resolveCrossAppHref('/home', 'marketing')   // -> /home (one origin)
 *   resolveCrossAppHref('/home', 'marketing')   // NEXT_PUBLIC_PLATFORM_URL set
 *                                               //   -> https://naumu.ai/home
 *   resolveCrossAppHref('/docs', 'marketing')   // same app -> /docs, untouched
 *   resolveCrossAppHref('mailto:hi@naumu.ai', 'platform') // untouched
 *
 * Untouched inputs: same-app paths, absolute URLs, protocol-relative hrefs,
 * `mailto:`/`tel:`, bare hashes, relative paths, and ANY foreign path while
 * `NEXT_PUBLIC_PLATFORM_URL` / `NEXT_PUBLIC_MARKETING_URL` are unset - which is
 * both the deployed case (`naumu.ai`) and normal local dev (the Traefik origin
 * `http://localhost:3000`), where a relative href is already correct.
 *
 * Query and hash ride along untouched: classification normalises them away,
 * but the returned href is the original string with the origin prepended.
 */
export const resolveCrossAppHref = (
	href: string,
	from: 'marketing' | 'platform',
): string => {
	const isForeign = from === 'marketing' ? isPlatformPath(href) : isMarketingPath(href);
	if (!isForeign) return href;
	const origin = foreignOrigin(from);
	return origin ? `${origin}${href}` : href;
};
